import React, { useEffect, useState } from "react";
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Divider,
  Button,
  Skeleton,
  IconButton,
  Alert,
} from "@mui/material";
import { useTranslations, useUser } from "hooks";
import { stripeApiTest } from "api";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InvoiceHistory from "./InvoiceHistory";
import useStripeBilling from "hooks/useStripeBilling";
import { getTranslation } from "common";

const MySubscriptions: React.FC = () => {
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState<string | null>(null);
  const { allSubscriptions, fetchSubscriptions, loading } = useStripeBilling();
  const translations = useTranslations()
  const user = useUser();

  useEffect(() => {
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.jwt]);

  //se la prendo da useStripeBilling mi da 422 (è il controllo !myOrganizations)
  const cancelSubscription = async (subscriptionId: string) => {
    setLocalLoading(true);
    try {
      const response = await stripeApiTest.post(`/stripe/subscription/cancel-pending-period-end?subscription_id=${subscriptionId}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${user?.jwt}`,
          },
        }
      );
      await fetchSubscriptions();

      return response.data;
    } catch (error) {
    } finally {
      setLocalLoading(false);
    }
  };

  const activeSubscriptions = allSubscriptions.filter((sub) => sub.active);
  const navigate = useNavigate()

  return (
    <Box marginTop={2}>
      <Accordion sx={{ marginBottom: 1, marginTop: 2 }} defaultExpanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5" fontWeight={600}>
            {getTranslation(translations, "billing.my_subscriptions")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {loading || localLoading ? <Grid container spacing={3}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid> :
            <Grid container spacing={2}>
              {activeSubscriptions.map((subscription) => (
                <Grid item xs={12} md={6} lg={4} key={subscription.id}>
                  <Card elevation={3} sx={{ minHeight: 284, marginBottom: 2 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="h6">
                            {subscription.subscription_rule?.name || ""}
                          </Typography>
                          <Typography variant="body2">
                            {getTranslation(translations, "placeholders.organization.name.label")}: {subscription.organization_name}
                          </Typography>
                        </Box>
                        {subscription.subscription_rule.provider_product_id !== process.env.REACT_APP_STRIPE_DEVELOPMENT_PROVIDER_PRODUCT_ID_FREE &&
                          <IconButton onClick={() => {
                            const invoices = subscription?.invoices;
                            if (invoices && invoices.length > 0) {
                              const lastInvoice = invoices[invoices.length - 1];
                              const invoiceUrl = lastInvoice?.pdf_url;
                              if (invoiceUrl) {
                                const link = document.createElement("a");
                                link.href = invoiceUrl;
                                link.setAttribute("download", "fattura.pdf");
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                              }
                            }
                          }} size="small">
                            <DownloadIcon />
                          </IconButton>
                        }
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      {subscription.invoices?.length > 0 && subscription.subscription_rule.provider_product_id !== process.env.REACT_APP_STRIPE_DEVELOPMENT_PROVIDER_PRODUCT_ID_FREE ? (
                        <Box mb={1}>
                          <Typography variant="subtitle2" gutterBottom>
                            {getTranslation(translations, "billing.subscription_invoice_details")}
                          </Typography>
                          {(() => {
                            const latestInvoice = [...subscription.invoices]
                              .sort((a, b) => new Date(b.invoice_date).getTime() - new Date(a.invoice_date).getTime())[0];
                            return (
                              <>
                                {/* <Typography variant="body2">
                                  <strong>Importo:</strong> {latestInvoice.amount}{latestInvoice.currency.toUpperCase()}
                                </Typography> */}
                                <Typography variant="body2">
                                  <strong>{getTranslation(translations, "subscription_active_from")}</strong>: {" "} {dayjs(subscription.subscription_date).format("DD/MM/YYYY")}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>{getTranslation(translations, "subscription_invoice_emission")}</strong>: {" "} {dayjs(latestInvoice.invoice_date).format("DD/MM/YYYY")}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>{getTranslation(translations, "subscription_payment_date")}</strong>: {" "}
                                  {latestInvoice.paid_date ? dayjs(latestInvoice.paid_date).format("DD/MM/YYYY") : "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>{getTranslation(translations, "generic.status")}: {" "}</strong>
                                  <span style={{ color: latestInvoice.status === "paid" ? "green" : "red" }}>
                                    {latestInvoice.status}
                                  </span>
                                </Typography>
                              </>
                            );
                          })()}
                        </Box>
                      ) : <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Alert severity="info" sx={{ mt: 2, alignItems: "center", fontSize: "12px" }} icon={false}>
                          {getTranslation(translations, "subscription.cta_more_features")}
                        </Alert>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigate(`/dashboard/organizations/${subscription.idorganization}/billing`, { state: { organizationName: subscription.organization_name } })}
                        >
                          {getTranslation(translations, "generic.upgrade")}
                        </Button>
                      </Box>}
                      {subscription.subscription_rule.provider_product_id !== process.env.REACT_APP_STRIPE_DEVELOPMENT_PROVIDER_PRODUCT_ID_FREE && subscription.status_history[0].status !== "cancel_pending_period_end" &&
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteOutlineIcon />}
                          sx={{
                            fontWeight: 500,
                            textTransform: "none",
                            borderRadius: 2,
                            px: 2,
                            py: 0.5,
                            "&:hover": {
                              backgroundColor: "rgba(244, 67, 54, 0.04)",
                              borderColor: "error.main",
                            },
                          }}
                          onClick={() => {
                            setSubscriptionToCancel(subscription.idsubscription);
                            setOpenConfirmDialog(true);
                          }}
                        >
                          {getTranslation(translations, "billing.cancel_subscription")}
                        </Button>
                      }
                      {subscription.status_history[0].status === "cancel_pending_period_end" && (<Typography><strong>{subscription.status_history[0].notes}</strong></Typography>)}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>}
        </AccordionDetails>
      </Accordion>
      <InvoiceHistory allSubscriptions={allSubscriptions} loading={loading} />

      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
      >
        <DialogTitle>{getTranslation(translations, "confirm_cancel.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {getTranslation(translations, "confirm_cancel_subscription.message")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="primary">
            {getTranslation(translations, "generic.button.cancel")}
          </Button>
          <Button
            onClick={async () => {
              if (subscriptionToCancel) {
                await cancelSubscription(subscriptionToCancel);
              }
              setOpenConfirmDialog(false);
            }}
            color="error"
            variant="contained"
          >
            {getTranslation(translations, "generic.button.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>);
};

export default MySubscriptions;
